/**
 * Bottom-right panel: the weapon slots with key hints and counts, the grenade throw-charge
 * meter, the first-aid count, and the rare gear (04 §6) - pistol and smoke slots that exist
 * only when carried, and tags for night-vision goggles, wire cutters and the ghillie wrap (ROUND7).
 */

import './weaponPanel.css';
import type { BindingsPort } from '../../input/bindings';
import { TICK_RATE } from '../../sim/types';
import type { WeaponId, World } from '../../sim/types';
import { clamp, el, show, Shown } from './dom';
import { keyHint, WEAPON_SLOTS } from './weaponSlots';

const CHARGE_W = 40;
/** Not in the contract: a grenade throw is assumed to reach full power after one second. */
const THROW_FULL_TICKS = TICK_RATE;

interface Slot {
  row: HTMLElement;
  key: HTMLElement;
  count: HTMLElement;
}

function setCount(slot: Slot, count: number, text = `x${count}`): void {
  slot.count.textContent = text;
  slot.row.classList.toggle('hud-slot-empty', count <= 0);
}

export class WeaponPanel {
  readonly el: HTMLElement;
  private readonly slots: Record<WeaponId, Slot>;
  private readonly aid: Slot;
  private readonly charge: HTMLElement;
  private readonly chargeFill: HTMLElement;
  private readonly gearRow: HTMLElement;
  private readonly nvg: HTMLElement;
  private readonly cutters: HTMLElement;
  private readonly ghillie: HTMLElement;
  private readonly shown = new Shown();
  /** Once carried, a gear slot stays (greyed at zero): an empty pistol is still a fact worth a line. */
  private hadPistol = false;
  private hadSmoke = false;
  private lastTick = -1;

  constructor(private readonly bindings?: BindingsPort) {
    const panel = el('div', 'hud-panel hud-br');
    const slot = (label: string, extraClass = ''): Slot => {
      const row = el('div', extraClass === '' ? 'hud-slot' : `hud-slot ${extraClass}`);
      const key = el('span', 'hud-key');
      const count = el('span', 'hud-slot-count');
      row.append(key, el('span', 'hud-slot-name', label), count);
      panel.append(row);
      return { row, key, count };
    };
    this.charge = el('div', 'hud-charge hud-hidden');
    const track = el('div', 'hud-charge-track');
    track.style.width = `${CHARGE_W + 2}px`;
    this.chargeFill = el('div', 'hud-charge-fill');
    track.append(this.chargeFill);
    this.charge.append(el('span', 'hud-charge-label', 'THROW'), track);

    const slots = {} as Record<WeaponId, Slot>;
    for (const spec of WEAPON_SLOTS) {
      slots[spec.weapon] = slot(spec.label, spec.gear ? 'hud-slot-gear hud-hidden' : '');
      // The charge meter sits directly under the grenade slot it belongs to.
      if (spec.weapon === 'grenade') panel.append(this.charge);
    }
    this.slots = slots;
    this.aid = slot('FIRST AID', 'hud-aid');

    this.nvg = el('span', 'hud-tag hud-tag-gear hud-hidden', 'NVG');
    this.cutters = el('span', 'hud-tag hud-tag-gear hud-hidden', 'CUTTERS');
    this.ghillie = el('span', 'hud-tag hud-tag-gear hud-hidden', 'GHILLIE');
    this.gearRow = el('div', 'hud-gear hud-hidden');
    this.gearRow.append(this.nvg, this.cutters, this.ghillie);
    panel.append(this.gearRow);
    this.el = panel;
    this.refreshKeys();
  }

  /** Re-read the key hints from the bindings: at start, and whenever a menu may have changed them. */
  refreshKeys(): void {
    const actions = this.bindings?.actions() ?? null;
    for (const spec of WEAPON_SLOTS) {
      const hint = keyHint(actions, spec.action, spec.fallbackKey);
      if (this.shown.changed(`key:${spec.weapon}`, hint)) this.slots[spec.weapon].key.textContent = hint;
    }
    const aid = keyHint(actions, 'firstAid', 'F');
    if (this.shown.changed('key:aid', aid)) this.aid.key.textContent = aid;
  }

  update(world: World): void {
    const shown = this.shown;
    const slots = this.slots;
    const ranger = world.ranger;
    const inv = ranger.inventory;
    const gear = ranger.gear;
    if (world.tick < this.lastTick) {
      this.hadPistol = false;
      this.hadSmoke = false;
    }
    this.lastTick = world.tick;

    if (shown.changed('weapon', ranger.weapon)) {
      for (const spec of WEAPON_SLOTS) {
        slots[spec.weapon].row.classList.toggle('hud-slot-on', ranger.weapon === spec.weapon);
      }
    }

    const reloading = ranger.reloadTicks > 0;
    if (shown.changed('carbine', reloading ? -1 : inv.roundsInMag * 1000 + inv.magazines)) {
      const carbine = slots.carbine;
      carbine.count.textContent = reloading ? 'RELOADING' : `${inv.roundsInMag}/${inv.magazines}`;
      carbine.row.classList.toggle('hud-slot-busy', reloading);
      carbine.row.classList.toggle('hud-slot-empty', !reloading && inv.roundsInMag <= 0 && inv.magazines <= 0);
    }
    if (shown.changed('grenades', inv.grenades)) setCount(slots.grenade, inv.grenades);
    if (shown.changed('laws', inv.laws)) setCount(slots.law, inv.laws);
    if (shown.changed('bombs', inv.bombs)) setCount(slots.bomb, inv.bombs);
    if (shown.changed('firstAid', inv.firstAid)) setCount(this.aid, inv.firstAid);

    // Gear: a slot appears with the item and never for a ranger who did not bring one.
    this.hadPistol ||= gear.pistolRounds > 0;
    this.hadSmoke ||= gear.smoke > 0;
    if (shown.changed('hasPistol', this.hadPistol)) show(slots.pistol.row, this.hadPistol);
    if (shown.changed('hasSmoke', this.hadSmoke)) show(slots.smoke.row, this.hadSmoke);
    if (shown.changed('pistol', gear.pistolRounds)) setCount(slots.pistol, gear.pistolRounds, `${gear.pistolRounds}RD`);
    if (shown.changed('smoke', gear.smoke)) setCount(slots.smoke, gear.smoke);
    if (shown.changed('nvg', gear.nvg)) show(this.nvg, gear.nvg);
    if (shown.changed('cutters', gear.wireCutters)) show(this.cutters, gear.wireCutters);
    const ghillie = gear.ghillie === true;
    if (shown.changed('ghillie', ghillie)) show(this.ghillie, ghillie);
    const anyTag = gear.nvg || gear.wireCutters || ghillie;
    if (shown.changed('gearRow', anyTag)) show(this.gearRow, anyTag);

    const charge = ranger.throwCharge > 0 ? clamp(ranger.throwCharge / THROW_FULL_TICKS, 0, 1) : 0;
    const chargePx = ranger.throwCharge > 0 ? Math.max(1, Math.round(charge * CHARGE_W)) : 0;
    if (shown.changed('charge', chargePx)) {
      show(this.charge, chargePx > 0);
      this.chargeFill.style.width = `${chargePx}px`;
      this.charge.classList.toggle('hud-charge-full', chargePx >= CHARGE_W);
    }
  }
}
